import { Sizes } from '@/lib/constants/sizes'
import {
	Drawer,
	Stack,
	Box,
	Divider,
	Button,
	SvgIcon,
	Typography,
	useTheme,
	IconButton,
	Fade,
	Container,
} from '@mui/material'
import React from 'react'
import { useNavigation } from '@/lib/context/navigationcontext'
import Image from 'next/image'
import { LeftPanelClose, LeftNavExpand } from '@/public/icons/menu_item'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { analyticsTags } from '@/app/lib/analytics/analyticsEventTags'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { Strings } from '@/lib/constants/strings'
import { useRouter } from 'next/navigation'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { trackLeftNavAnalyticsEvents } from './leftnavanalytics'
import NavItem, { NavIds, LeftNavProps } from './nav'
import { useNavItems } from './useNavItems'
import CompanyName from './companyName'

export default function LeftNavDesk({
	setOpen,
	changeFeaturePage,
	isTncSelected,
	setIsTncSelected,
	navItemSx,
}: Readonly<LeftNavProps>) {
	const theme = useTheme()
	const { tempBoss1863EnableCannyWidget } = useFlags()

	const {
		isLeftNavExpanded,
		toggleLeftNavExpanded,
		selectedNavItem,
		setSelectedNavItem,
		setSelectedAppBarNavItem,
		setSelectedNavItemBeforeSignout,
	} = useNavigation()

	const router = useRouter()
	const toggleNav = () => {
		if (isLeftNavExpanded !== undefined && isLeftNavExpanded !== null) {
			toggleLeftNavExpanded?.(isLeftNavExpanded)
			if (isLeftNavExpanded) {
				pushToDataLayer(analyticsTags.leftNavCollapse)
			} else {
				pushToDataLayer(analyticsTags.leftNavExpand)
			}
		} else {
			throw new Error('isLeftNavExpanded is not defined')
		}
	}

	const handleNavMenuClick = (item: NavItem) => {
		setIsTncSelected(false)

		// Use navId for analytics
		trackLeftNavAnalyticsEvents(item.navId)

		if (item.navId === NavIds.Signout) {
			setSelectedNavItem(item)
			setSelectedNavItemBeforeSignout(selectedNavItem)
			setSelectedAppBarNavItem(selectedNavItem)
			setOpen(true)
		} else {
			setSelectedNavItem(item)
			setSelectedAppBarNavItem(item)
			changeFeaturePage(item.navId)
		}
	}

	const navItems = useNavItems()
	return (
		<Drawer
			variant={'permanent'}
			anchor="left"
			PaperProps={{ elevation: 1 }}
			sx={{
				'display': { xs: 'none', sm: 'block' },
				'& .MuiDrawer-paper': {
					width: isLeftNavExpanded
						? `${Sizes.LEFT_DRAWER_WIDTH}px`
						: `${Sizes.LEFT_DRAWER_COLLAPSE_WIDTH}px`,
					overflow: 'hidden',
				},
				'& .MuiPaper-elevation': { overflow: 'hidden' },
			}}
			data-testid="left-nav-desk"
		>
			<Stack spacing={0}>
				<Stack display={'flex'}>
					<Stack
						direction={isLeftNavExpanded ? 'row' : 'column'}
						alignItems={'center'}
					>
						<Container
							sx={{
								mt: 2,
								mb: 2,
								position: 'relative',
								height: isLeftNavExpanded ? '42px' : '40px',
							}}
						>
							<Container sx={{ position: 'absolute', top: 0, left: 0 }}>
								<Fade in={isLeftNavExpanded}>
									<Image
										alt="logo"
										src="/icons/bi_icons/broker-workplace_nav_expanded.svg"
										width={128}
										height={48}
									/>
								</Fade>
							</Container>
							<Container sx={{ position: 'absolute', top: 0, left: 0 }}>
								<Fade in={!isLeftNavExpanded}>
									<Image
										alt="emblem"
										src="/icons/bi_icons/broker-workplace_nav_collapsed.svg"
										width={52}
										height={32}
									/>
								</Fade>
							</Container>
						</Container>
						<Stack
							direction={isLeftNavExpanded ? 'row' : 'column'}
							justifyContent={'flex-end'}
							flexGrow={1}
							justifyItems={isLeftNavExpanded ? 'flex-end' : 'center'}
							alignItems={'center'}
						>
							{isLeftNavExpanded ? (
								<IconButton
									data-testid="toggle-nav-icon-expanded"
									aria-label="toggle nav icon expanded"
									onClick={toggleNav}
									disableRipple
								>
									<LeftPanelClose />
								</IconButton>
							) : (
								<IconButton
									data-testid="toggle-nav-icon-collapsed"
									aria-label="toggle nav icon collapsed"
									onClick={toggleNav}
									disableRipple
									style={{}}
								>
									<LeftNavExpand />
								</IconButton>
							)}
						</Stack>
					</Stack>
				</Stack>
				{isLeftNavExpanded && <CompanyName />}
				{navItems
					.filter(item => item.show)
					.map(
						(
							{
								label,
								labelMini,
								iconSelected,
								iconDeselected,
								wrapText,
								navId,
							}: NavItem,
							index,
							array
						) => {
							const selected = selectedNavItem?.navId === navId
							const selectedColor = selected
								? theme.palette.foreground.brand.primary
								: theme.palette.foreground.neutral.onSurfaceVar02
							const icon = selected ? iconSelected : iconDeselected
							const isLastItem =
								index === array.length - (tempBoss1863EnableCannyWidget ? 2 : 1)
							return (
								<React.Fragment key={label}>
									<Stack spacing={1} data-testid="nav-button">
										{isLastItem && (
											<Stack>
												<Divider />
											</Stack>
										)}
										<Button
											variant="nav"
											color={selected ? 'selected' : 'default'}
											size={isLeftNavExpanded ? 'expanded' : 'collapsed'}
											disableElevation
											data-testid={`nav-icon-button-${index}`}
											startIcon={
												isLeftNavExpanded ? (
													<SvgIcon
														component={icon}
														data-testid="startIcon"
														aria-label={`${icon}`}
														style={{
															color: selectedColor,
															width: 40,
															height: 24,
															transform: 'translateY(3px)',
														}}
													/>
												) : null
											}
											onClick={() => handleNavMenuClick(navItems[index])}
											sx={navItemSx(selected)}
										>
											{!isLeftNavExpanded && (
												<SvgIcon
													component={icon}
													style={{
														color: selectedColor,
														width: 40,
														height: 24,
														alignSelf: 'middle',
														paddingBottom: 3,
													}}
												/>
											)}
											<Typography
												variant={TypographyVariants.smallRegular}
												data-testid={`nav-icon-${label}`}
												noWrap={!wrapText}
											>
												{isLeftNavExpanded ? label : labelMini}
											</Typography>
										</Button>
									</Stack>
								</React.Fragment>
							)
						}
					)}
			</Stack>
			<Box
				sx={{
					width: isLeftNavExpanded
						? `${Sizes.LEFT_DRAWER_WIDTH}px`
						: `${Sizes.LEFT_DRAWER_COLLAPSE_WIDTH}px`,
					position: 'fixed',
					bottom: 0,
					left: 0,
					pb: 4,
				}}
			>
				<Button
					variant="nav"
					data-testid="tnc-nav-button"
					onClick={() => {
						setIsTncSelected(true)
						pushToDataLayer(analyticsTags.biViewTermsAndConditions)
						router.push('/feature/termsandconditions')
					}}
					color={isTncSelected ? 'selected' : 'default'}
					sx={{
						...navItemSx(isTncSelected),
						p: '10px 12px',
						width: '100%',
						justifyContent: 'flex-start',
					}}
				>
					<Typography
						variant={
							isLeftNavExpanded
								? TypographyVariants.smallRegular
								: TypographyVariants.extraSmallRegular
						}
					>
						{Strings.TNC_NAV_TITLE}
					</Typography>
				</Button>
			</Box>
		</Drawer>
	)
}
